// =============================================
// MAIN APPLICATION ENTRY POINT
// Bootstraps 3D scene, wires UI, manages state
// =============================================
import * as THREE from 'three';
import { initScene, animate, setPartClickHandler, setViewMode, highlightPart, clearHighlights, setXRayMode, getCamera, getScene, setDOFFocus, setLayerLevel, setSystemIsolation, setViewPreset, setHousingOpacity, snapCameraToPart, simulateSymptom, setAutoRotate } from './scene.js';
import { buildCarburetorModel, LABEL_POSITIONS, LABEL_DATA } from './carburetor/model.js';
import { PARTS_DATA, getPartData, getSystemColor } from './carburetor/parts-data.js';
import { drawFuelSchematic, getFuelComponentAt, FUEL_COMPONENTS, FUEL_ANIMATIONS_DATA } from './fuel-system/fuel-system.js';
import { setupLibrary } from './reference-library.js';
import { setupFlashcardPage } from './flashcards/flashcards.js';
import { SYMPTOMS, renderSymptomList, renderDiagnosis } from './troubleshooting/troubleshooting.js';
import { renderGlossary } from './glossary/glossary.js';
import { startQuiz, getCurrentQuestion, submitAnswer, nextQuestion, getHint, getAnalytics, getTopicMastery, getDifficultyPerformance, getLearningProgression, getQuestionCount, resetAnalytics, getWrongAnswers, CATEGORIES, initQuizForUser } from './quiz/quiz.js';
import { renderValveCards, renderValveDetail, VALVES_DATA } from './valves/valves.js';
import { DIAGRAMS_DATA, PART_TO_DIAGRAM } from './carburetor/diagrams-data.js';
import { isFirstVisit, setUserName, getUserName, getGreeting, recordVisit, getProfile, resetProfile, updateUserPageVisit, updateUserPageTime, updateUserFeature, recordUserSession, syncQuizToUser, syncFlashcardsToUser, ensureGuestSession, getCurrentUserId } from './user-profile.js';
import { getMaintenanceMode, listenToAlerts, logTelemetry, listenToConfig } from './firebase.js';


// ===== STATE =====
const state = {
    currentPage: 'carburetor',
    viewMode: 'external',
    mode: 'study',
    labelsVisible: true,
    flowVisible: false,
    pressureVisible: false,
    selectedPart: null,
    processPlaying: false,
    currentProcess: null,
    fuelAnim: 'normal',
    fuelSelectedComponent: null,
    animationTime: 0,
    blueprintActive: false,
    blueprintZoom: 1,
    blueprintPan: { x: 0, y: 0 },
    currentDiagram: 'carb-section',
    throttle: 25,
    mixture: 85,
    autoRotate: true,
};

let lastInteractionTime = Date.now();
let mainControls = null; // Store reference to 3D controls
let rotateTimer = null;

let isPanning = false;
let startPanMouse = { x: 0, y: 0 };
let startPanOffset = { x: 0, y: 0 };

// highlightPart is imported from scene.js — used for part selection & quiz hints

// ===== INITIALIZATION =====
document.addEventListener('DOMContentLoaded', () => {
    initApp();
});

function initApp() {
    const canvas = document.getElementById('three-canvas');
    if (!canvas) return;

    // MAINTENANCE CHECK
    getMaintenanceMode().then(status => {
        if (status && status.active) {
            handleMaintenanceUpdate(status);
        }
    });

    // Init 3D scene
    const { scene, controls } = initScene(canvas);
    mainControls = controls;

    // CRITICAL: Ensure we have a session ID immediately (Pre-Registration Tracking)
    ensureGuestSession();
    recordVisit();

    // Build and add carburetor model
    const carbModel = buildCarburetorModel();
    scene.add(carbModel);

    // Set up part click handler
    setPartClickHandler((partId, object, hitPoint) => {
        if (partId) {
            selectPart(partId);
            if (hitPoint) setDOFFocus(hitPoint);
        } else {
            document.getElementById('info-panel').classList.remove('visible');
            state.selectedPart = null;
            clearHighlights();
            setDOFFocus(null);
        }
    });

    // Label Interaction: Clicking 3D labels shows info
    const overlay = document.getElementById('label-overlay');
    if (overlay) {
        overlay.addEventListener('click', (e) => {
            const label = e.target.closest('.part-label');
            if (label) {
                const pid = label.dataset.partId;
                selectPart(pid);
                // Auto-focus camera on the part when label is clicked
                const labelInfo = LABEL_DATA[pid];
                if (labelInfo) setDOFFocus(labelInfo.anchor);
            }
        });
    }


    // Set up navigation
    setupNavigation();
    setupToolbar();
    setupModes();
    setupAutoRotateTracker();
    setupProcessPanel();
    setupPowerplantLab();
    setupFuelSystem();
    setupTroubleshooting();
    setupGlossary();
    setupQuiz();
    setupValves();
    setupLibrary();
    setupFlashcardPage();
    setupInfoPanel();
    setupMobileMenuHint();
    setupMissionCommand();

    // Create 3D labels
    updateLabels();

    // Set initial view mode
    setViewMode('transparent');

    // Start animation loop
    let lastTime = 0;
    animate((time) => {
        const delta = time - lastTime;
        state.animationTime = time;
        
        // Throttle updates to ~30fps for labels/schematics if delta < 32ms
        // Particles keep full rate for smoothness if possible
        updateFlowParticles(time);
        
        if (delta > 32) {
            updateLabels();
            updateFuelSchematic(time);
            updateValveAnimations(time);
            lastTime = time;
        }
    });

    // Draw initial inset canvases
    drawInsetSchematics();

    // Wire new controls
    setupSystemIsolation();
    setupLayerSlider();
    setupCameraPresets();
    setupCameraHUD();
    setupBlueprintMode();

    // User personalization
    setupWelcomeModal();
    updateGreeting();
    recordVisit();

    // Ensure quiz data matches this user
    initQuizForUser();

    // Sync flashcard progress into user registry for admin dashboard
    syncFlashcardsToUser();

    // Usage analytics tracking
    initUsageTracker();

    // Mobile analytics drawer
    setupMobileAnalytics();

    // FINAL BOOTSTRAP GATE: Ensure no ghost blur filters are active
    document.getElementById('app')?.classList.remove('blurred');
}

// ===== NAVIGATION =====
function setupNavigation() {
    // Hamburger menu toggle
    const hamburger = document.getElementById('hamburger-menu');
    const topBar = document.getElementById('top-bar');
    if (hamburger && topBar) {
        hamburger.addEventListener('click', () => {
            topBar.classList.toggle('open');
            // Dismiss hint if visible
            const dot = document.querySelector('.menu-hint-dot');
            const hint = document.getElementById('mobile-menu-hint');
            if (dot) dot.classList.add('hidden');
            if (hint) {
                hint.classList.add('fade-out');
                setTimeout(() => hint.classList.add('hidden'), 500);
            }
            localStorage.setItem('carb-mobile-menu-hint-seen', 'true');
        });
    }

    document.querySelectorAll('.nav-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const page = btn.dataset.page;
            switchPage(page);
            // Auto-close menu on mobile after selection
            if (topBar) topBar.classList.remove('open');
        });
    });
}

function switchPage(page) {
    state.currentPage = page;

    document.querySelectorAll('.nav-btn').forEach(b => b.classList.toggle('active', b.dataset.page === page));
    document.querySelectorAll('.page').forEach(p => p.classList.toggle('active', p.id === `page-${page}`));

    // Isolation handling
    if (page === 'valves') {
        setHousingOpacity(0.2); // Dim housing
        setupValves();
    } else {
        setHousingOpacity(1.0); // Restore housing
        clearHighlights();
    }

    if (page === 'troubleshooting') {
        setupTroubleshooting();
    }

    if (page === 'library') {
        // Any specific re-renders for library can go here
    }

    if (page === 'flashcards') {
        setupFlashcardPage();
    }

    if (page === 'fuel-system') {
        state.currentDiagram = 'fuel-detailed';
    } else {
        state.currentDiagram = 'carb-section';
    }

    if (page !== 'carburetor') {
        const infoPanel = document.getElementById('info-panel');
        if (infoPanel) infoPanel.classList.remove('visible');
    }

    // Force close mobile analytics on page switch
    document.getElementById('quiz-analytics')?.classList.remove('mobile-active');
    document.querySelector('.fc-sidebar')?.classList.remove('mobile-active');

    // Toggle analytics button visibility (only needed on Quiz and Flashcards)
    const analyticsBtn = document.getElementById('analytics-toggle');
    if (analyticsBtn) {
        if (page === 'quiz' || page === 'flashcards') {
            analyticsBtn.classList.add('visible');
            
            // Mobile Analytics Discovery Hint
            if (window.innerWidth <= 960 && !localStorage.getItem('carb-hint-analytics-mobile')) {
                const hint = document.createElement('div');
                hint.className = 'interaction-hint';
                hint.innerHTML = `
                    <div class="hint-body">
                        <span class="hint-arrow-icon">📊</span>
                        <span>Open floating analytics for performance data</span>
                    </div>
                    <div class="hint-sub">MISSION ACCOMPLISHED: TRACKER ACTIVE</div>
                `;
                document.body.appendChild(hint);
                localStorage.setItem('carb-hint-analytics-mobile', 'true');
                setTimeout(() => hint.remove(), 3000);
            }
        } else {
            analyticsBtn.classList.remove('visible');
        }
    }

    // Auto-scroll logic for mobile stacking
    if (window.innerWidth <= 960) {
        if (page === 'fuel-system') autoScrollTo('#fuel-info-panel');
        if (page === 'troubleshooting') autoScrollTo('#symptom-list');
        if (page === 'flashcards') autoScrollTo('.fc-main');
    }

    // INTERACTION HINTS
    const hintKey = 'carb-hint-v2-' + page;
    if (!localStorage.getItem(hintKey)) {
        let msg = "";
        let sub = "SYSTEM INTELLIGENCE";
        if (page === 'carburetor') { msg = "Tap hologram nodes for technical specs"; sub = "3D DIAGNOSTIC INTERFACE"; }
        if (page === 'fuel-system') msg = "Double-click components for fuel drill-down";
        if (page === 'troubleshooting') msg = "Select symptoms to bridge 3D diagnostics";
        if (page === 'valves') msg = "Tap valve nodes to trigger system isolation";
        
        if (msg) {
            const hint = document.createElement('div');
            hint.className = 'interaction-hint';
            hint.innerHTML = `
                <div class="hint-body">
                    <span class="hint-arrow-icon">▶</span>
                    <span>${msg}</span>
                </div>
                <div class="hint-sub">${sub}</div>
            `;
            document.body.appendChild(hint);
            localStorage.setItem(hintKey, 'true');
            setTimeout(() => hint.remove(), 3000);
        }
    }

    logTelemetry(`Pilot switched to module: ${page.toUpperCase()}`);
}

function setupMissionCommand() {
    // 1. Listen for Global Fleet Alerts
    listenToAlerts((alert) => {
        if (!alert || !alert.message) return;
        
        // Don't show the same alert twice
        const lastId = localStorage.getItem('last_alert_id');
        if (lastId === alert.id) return;
        localStorage.setItem('last_alert_id', alert.id);

        showTacticalAlert(alert.message);
    });

    // 2. Listen for Maintenance & Systems Status
    listenToConfig((config) => {
        if (config.type === 'status') {
            handleMaintenanceUpdate(config.data);
        } else if (config.type === 'toggles') {
            handleSystemsToggles(config.data);
        }
    });

    // Initial check for maintenance
    getMaintenanceMode().then(handleMaintenanceUpdate);
}

function showTacticalAlert(msg) {
    const el = document.createElement('div');
    el.className = 'tactical-alert';
    el.innerHTML = `
        <div class="ta-icon">🛰️</div>
        <div class="ta-body">
            <div class="ta-header">TACTICAL BROADCAST</div>
            <div class="ta-msg">${msg}</div>
        </div>
    `;
    document.body.appendChild(el);
    
    // Play sound if possible
    try { new Audio('/alert.mp3').play(); } catch(e) {}

    setTimeout(() => {
        el.classList.add('fade-out');
        setTimeout(() => el.remove(), 1000);
    }, 8000);
}

function handleMaintenanceUpdate(status) {
    if (!status || !status.active) {
        const modal = document.getElementById('maintenance-overlay');
        if (modal) {
            modal.classList.add('hidden');
            // If it's the dynamic one, we might want to remove it, but adding 'hidden' is safer if it's the HTML one
            // modal.remove(); 
        }
        document.body.classList.remove('offline');
        document.getElementById('app')?.classList.remove('blurred');
        return;
    }

    // Show Lockdown Screen
    document.body.classList.add('offline');
    document.getElementById('app')?.classList.add('blurred');
    let overlay = document.getElementById('maintenance-overlay');
    if (overlay) overlay.classList.remove('hidden');
    else {
        overlay = document.createElement('div');
        overlay.id = 'maintenance-overlay';
        document.body.appendChild(overlay);
    }
    
    overlay.innerHTML = `
        <div class="m-content">
            <div class="m-icon">🛑</div>
            <h1>SYSTEMS OFFLINE</h1>
            <div class="m-divider"></div>
            <p>${status.message || "Scheduled maintenance in progress. Please check back later."}</p>
            <div class="m-footer">STRATEGIC COMMAND HUB · LOCKDOWN ACTIVE</div>
        </div>
    `;
}

function handleSystemsToggles(toggles) {
    if (!toggles) return;
    
    // Toggle navigation buttons
    const navItems = {
        'quiz': 'nav-quiz',
        'library': 'nav-library',
        'flashcards': 'nav-flashcards',
        'admin-link': 'nav-admin'
    };

    Object.entries(navItems).forEach(([key, btnId]) => {
        const isEnabled = toggles[key] !== false; // Active by default
        const btn = document.getElementById(btnId);
        if (btn) {
            btn.style.display = isEnabled ? 'flex' : 'none';
        }
        
        // If user is currently on a disabled page, kick them back to home
        if (!isEnabled && state.currentPage === key) {
            switchPage('carburetor');
        }
    });
}

// ===== MOBILE ANALYTICS TOGGLE =====
function setupMobileAnalytics() {
    const toggle = document.getElementById('analytics-toggle');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
        const quizAnalytics = document.getElementById('quiz-analytics');
        const flashcardSidebar = document.querySelector('.fc-sidebar');
        
        if (state.currentPage === 'quiz' && quizAnalytics) {
            quizAnalytics.classList.toggle('mobile-active');
            document.getElementById('app').classList.toggle('blurred', quizAnalytics.classList.contains('mobile-active'));
            if (quizAnalytics.classList.contains('mobile-active')) drawAnalyticsCharts();
        } else if (state.currentPage === 'flashcards' && flashcardSidebar) {
            flashcardSidebar.classList.toggle('mobile-active');
        }
    });

    // Close when clicking outside on mobile
    document.addEventListener('click', (e) => {
        if (window.innerWidth > 1024) return;
        if (e.target.closest('#analytics-toggle')) return;
        
        if (!e.target.closest('.quiz-analytics') && !e.target.closest('.fc-sidebar')) {
            const qa = document.getElementById('quiz-analytics');
            if (qa?.classList.contains('mobile-active')) {
                qa.classList.remove('mobile-active');
                document.getElementById('app').classList.remove('blurred');
            }
            document.querySelector('.fc-sidebar')?.classList.remove('mobile-active');
        }
    });
}

// ===== MOBILE AUTO-SCROLL HELPER =====
function autoScrollTo(selector, offset = 80) {
    if (window.innerWidth > 960) return;
    const el = document.querySelector(selector);
    if (!el) return;
    
    setTimeout(() => {
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
    }, 100);
}

// ===== TOOLBAR =====
function setupToolbar() {
    // View mode buttons
    document.querySelectorAll('.tool-btn[data-view]').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.tool-btn[data-view]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            state.viewMode = btn.dataset.view;
            setViewMode(state.viewMode);
        });
    });

    // Toggle buttons
    document.querySelectorAll('.tool-btn[data-toggle]').forEach(btn => {
        btn.addEventListener('click', () => {
            btn.classList.toggle('active');
            const toggle = btn.dataset.toggle;

            switch (toggle) {
                case 'flow':
                    state.flowVisible = btn.classList.contains('active');
                    break;
                case 'blueprint':
                    toggleBlueprintMode(btn.classList.contains('active'));
                    break;
                case 'labels':
                    state.labelsVisible = btn.classList.contains('active');
                    updateLabels();
                    break;
                case 'pressure':
                    state.pressureVisible = btn.classList.contains('active');
                    document.getElementById('pressure-legend').classList.toggle('hidden', !state.pressureVisible);
                    break;
                case 'fuel-inset':
                    document.getElementById('fuel-inset').classList.toggle('hidden');
                    break;
                case 'air-inset':
                    document.getElementById('air-inset').classList.toggle('hidden');
                    break;
            }
        });
    });
}

// ===== SYSTEM ISOLATION =====
function setupSystemIsolation() {
    document.querySelectorAll('.tool-btn[data-system]').forEach(btn => {
        btn.addEventListener('click', () => {
            const wasActive = btn.classList.contains('active');
            // Deactivate all system buttons
            document.querySelectorAll('.tool-btn[data-system]').forEach(b => b.classList.remove('active'));
            if (!wasActive) {
                btn.classList.add('active');
                setSystemIsolation(btn.dataset.system);
            } else {
                setSystemIsolation(null);
            }
        });
    });
}

// ===== LAYER SLIDER =====
function setupLayerSlider() {
    const slider = document.getElementById('layer-slider');
    const label = document.getElementById('layer-label');
    if (!slider) return;

    const layerLabels = { '1': 'Shell', '2': 'Cavities', '3': 'All' };

    slider.addEventListener('input', () => {
        const level = parseInt(slider.value);
        setLayerLevel(level);
        if (label) label.textContent = layerLabels[slider.value] || 'All';
    });
}

// ===== QUICK ACTIONS =====
function setupCameraPresets() {
    // Reset View button
    document.getElementById('btn-reset-view')?.addEventListener('click', () => {
        setViewPreset('3d');
        setHousingOpacity(1.0);
        clearHighlights();
        state.selectedPart = null;
        document.getElementById('info-panel')?.classList.remove('visible');
        
        // Reset elevation slider
        const elevSlider = document.getElementById('cam-elevation');
        if (elevSlider) elevSlider.value = 50;
    });

    // Auto-Rotate button
    document.getElementById('btn-auto-rotate')?.addEventListener('click', () => {
        state.autoRotate = !state.autoRotate;
        const btn = document.getElementById('btn-auto-rotate');
        if (state.autoRotate) {
             btn?.classList.add('active');
        } else {
             btn?.classList.remove('active');
             setAutoRotate(false); // Stop immediately if toggled off
        }
    });

    // Screenshot button
    document.getElementById('btn-screenshot')?.addEventListener('click', () => {
        const canvas = document.getElementById('three-canvas');
        if (!canvas) return;
        try {
            const link = document.createElement('a');
            link.download = `carburetor-${Date.now()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (e) {
            console.warn('Screenshot failed:', e);
        }
    });
}

// ===== CAMERA HUD =====
function setupCameraHUD() {
    const elevSlider = document.getElementById('cam-elevation');
    if (!elevSlider) return;
    
    // Bind to the new scene handler
    elevSlider.addEventListener('input', () => {
        // Any manual adjustment stops the auto-showcase
        stopAutoShowcase();
        
        import('./scene.js').then(m => {
            m.setCameraElevation(parseInt(elevSlider.value));
        });
    });
}

// ===== MODES =====
function setupModes() {
    document.querySelectorAll('.mode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            // Blueprint mode is a toggle, not exclusive with the others
            if (btn.id === 'blueprint-mode-btn') {
                btn.classList.toggle('active');
                state.blueprintActive = btn.classList.contains('active');
                const bpPanel = document.getElementById('blueprint-panel');
                if (state.blueprintActive) {
                    bpPanel.classList.remove('hidden');
                    renderDiagram(state.currentDiagram || 'carb-section');
                } else {
                    bpPanel.classList.add('hidden');
                }
                return;
            }

            // For Study/Exam/Process — toggle off the other modes (not blueprint)
            document.querySelectorAll('.mode-btn[data-mode]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            state.mode = btn.dataset.mode;
            applyMode();
        });
    });
}

function applyMode() {
    const processPanel = document.getElementById('process-panel');
    const mixtureLab = document.getElementById('mixture-lab');

    switch (state.mode) {
        case 'study':
            state.labelsVisible = true;
            processPanel.classList.add('hidden');
            mixtureLab.classList.add('hidden');
            state.processPlaying = false;
            state.processActive = false;
            clearFlowParticles();
            updateLabels();
            // Restore housing when leaving process mode
            import('./scene.js').then(m => m.setHousingOpacity(1.0));
            break;
        case 'exam':
            state.labelsVisible = false;
            processPanel.classList.add('hidden');
            mixtureLab.classList.add('hidden');
            state.processPlaying = false;
            state.processActive = false;
            clearFlowParticles();
            updateLabels();
            import('./scene.js').then(m => m.setHousingOpacity(1.0));
            break;
        case 'process':
            processPanel.classList.remove('hidden');
            // If we are switching TO process mode and mixture lab happens to be active, trigger aesthetic
            const isLab = !document.getElementById('mixture-lab').classList.contains('hidden');
            import('./scene.js').then(m => m.setLabAesthetic(isLab));

            // Hint for Process Mode
            if (!localStorage.getItem('carb-hint-process-v2')) {
                const hint = document.createElement('div');
                hint.className = 'interaction-hint';
                hint.innerHTML = `
                    <div class="hint-body">
                        <span class="hint-arrow-icon">▶</span>
                        <span>Use scrubber to analyze fluid dynamics</span>
                    </div>
                    <div class="hint-sub">DATALINK: OPERATION TIMELINE</div>
                `;
                document.body.appendChild(hint);
                localStorage.setItem('carb-hint-process-v2', 'true');
                setTimeout(() => hint.remove(), 3000);
            }
            break;
    }
}

// ===== PROCESS PANEL =====
function setupProcessPanel() {
    document.querySelectorAll('.process-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.process-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            state.currentProcess = btn.dataset.process;

            // Build flow particles for this process
            buildFlowParticles(btn.dataset.process);
             // Auto-play
             state.processPlaying = true;
             state.processActive = true; // Signal for scene to dim
             
             const ppBtn = document.getElementById('play-pause-btn');
             if (ppBtn) ppBtn.innerHTML = '<span>⏸️</span>';
             processTime = 0;
 
             // Snap camera and dim housing for visibility
             import('./scene.js').then(m => {
                 m.setHousingOpacity(0.15);
                 
                 // Contextual zoom based on process
                 const p = btn.dataset.process;
                 let preset = 'textbook';
                 if (p === 'cruise' || p === 'full-power') preset = 'process_cruise';
                 if (p === 'idle') preset = 'process_idle';
                 if (p === 'mixture') preset = 'process_mixture';
                 if (p.includes('accel')) preset = 'process_accel';
                 if (p.includes('float') || p.includes('needle')) preset = 'process_float';
                 if (p.includes('jet')) preset = 'process_jets';
                 if (p.includes('air-bleed')) preset = 'process_air_bleed';
                 
                 m.setViewPreset(preset);
             });

            if (btn.dataset.process === 'mixture') {
                document.getElementById('mixture-lab').classList.remove('hidden');
                import('./scene.js').then(m => m.setLabAesthetic(true));
            } else {
                document.getElementById('mixture-lab').classList.add('hidden');
                import('./scene.js').then(m => m.setLabAesthetic(false));
            }

            updateProcessDescription();

            // Mobile Auto-scroll
            autoScrollTo('#process-description-area', 20);
        });
    });

    // Play/Pause
    const ppBtn = document.getElementById('play-pause-btn');
    ppBtn?.addEventListener('click', (e) => {
        e.stopPropagation(); // Don't let click pass to canvas
        state.processPlaying = !state.processPlaying;
        ppBtn.innerHTML = state.processPlaying ? '<span>⏸️</span>' : '<span>▶️</span>';
        
        // Failsafe: Re-enable controls if they got stuck
        import('./scene.js').then(m => {
            const controls = m.getRenderer()?.domElement?._controls; // if we can access it
            // Or just call update
            m.setHousingOpacity(state.processActive ? 0.3 : 1.0);
        });
    });

    // Speed button
    document.getElementById('speed-btn')?.addEventListener('click', () => {
        const speeds = [1, 2, 0.5];
        const labels = ['1×', '2×', '0.5×'];
        const idx = speeds.indexOf(playSpeed);
        const nextIdx = (idx + 1) % speeds.length;
        playSpeed = speeds[nextIdx];
        document.getElementById('speed-btn').textContent = labels[nextIdx];
    });

    // Timeline slider manual seek
    document.getElementById('timeline-slider')?.addEventListener('input', (e) => {
        processTime = parseFloat(e.target.value) / 100;
    });
}

const PROCESS_DESCRIPTIONS = {
    'cruise': `<strong>Normal Cruise Operation:</strong> Fuel enters the carb from the aircraft fuel system, flows through the inlet strainer, past the needle valve into the float bowl. The float maintains a constant fuel level. Air enters through the air inlet, accelerates through the venturi — pressure drops at the throat. This low pressure draws fuel from the bowl through the main metering jet. Air from the main air bleed mixes with the fuel to create an emulsion. The emulsion exits the main discharge nozzle into the venturi airstream. The throttle valve is partially open. The fuel-air mixture continues through the intake manifold to the cylinders.`,
    'idle': `<strong>Idle & Transition:</strong> At idle, the throttle plate is nearly closed. Very little air passes through the venturi, so the venturi pressure drop is minimal. Instead, the high manifold vacuum downstream of the throttle plate draws fuel through the idle circuit — via the idle jet, idle air bleed, and idle discharge port. As the throttle opens from idle, progression holes are progressively exposed to vacuum, bridging fuel delivery from idle circuit to main metering. The accelerator pump provides a brief fuel shot to cover any lean spots during rapid opening.`,
    'full-power': `<strong>Full Power & Economizer:</strong> At wide-open throttle, maximum airflow passes through the venturi creating a strong pressure drop. The main jet delivers maximum fuel flow. To prevent the mixture from being dangerously lean at full power (which could cause detonation), the economizer valve opens, providing additional fuel through a supplementary passage. This enriches the mixture for cylinder cooling and detonation protection.`,
    'mixture': `<strong>Mixture Control Lab:</strong> Use the slider below to adjust mixture from Full Rich to Idle Cutoff. Watch how the mixture control valve changes the effective fuel passage size, and observe the effect on RPM, EGT, and cylinder head temperature.`,
    'accel': `<strong>Accelerator Pump — Working:</strong> Watch a rapid throttle slam from idle to full power. The accelerator pump's cam is activated by the throttle shaft, pushing the pump diaphragm. The inlet check valve closes, the outlet check valve opens, and a precise shot of fuel is injected into the venturi. The engine transitions smoothly with no hesitation.`,
    'accel-fail': `<strong>Accelerator Pump — Failed:</strong> Same rapid throttle movement, but the pump diaphragm is worn and cannot build pressure. No supplemental fuel is delivered. Airflow increases immediately but fuel response lags — the engine experiences a momentary lean stumble or hesitation before the main system catches up.`,
    'float-stuck-high': `<strong>⚠️ Float Stuck High (Down Position):</strong> The float is stuck in the lowered position, holding the needle valve open. Fuel continuously floods into the bowl beyond the normal level. Excess fuel overflows through the discharge nozzle and drains. Symptoms: extremely rich mixture, black smoke, fuel dripping from carburetor, hard starting.`,
    'float-leaking': `<strong>⚠️ Float Leaking (Heavy):</strong> The float has a crack or pinhole and is filling with fuel, becoming heavier. It sits lower than normal, maintaining a higher-than-correct fuel level. Mixture runs chronically rich at all power settings. Sooty plugs, high fuel consumption, possible overflow.`,
    'needle-not-sealing': `<strong>⚠️ Needle Valve Not Sealing:</strong> Wear, corrosion, or debris prevents the needle from fully seating against the valve seat. Fuel seeps past even when the float pushes the needle into the seat. Bowl fuel level creeps higher than normal, causing progressive enrichment and possible flooding.`,
    'main-jet-blocked': `<strong>⚠️ Main Jet Partially Blocked:</strong> Varnish, sediment, or debris partially obstructs the main metering jet. At idle (where the main jet isn't used), the engine runs normally. At cruise and above, insufficient fuel is delivered — mixture runs lean. High EGT, roughness, power loss, risk of detonation.`,
    'idle-jet-blocked': `<strong>⚠️ Idle Jet Blocked:</strong> The idle fuel restriction is clogged. At cruise and above (where the main system provides fuel), the engine runs fine. But at idle, no fuel can reach the idle discharge port. The engine dies or runs extremely rough when throttle is brought to idle.`,
    'air-bleed-blocked': `<strong>⚠️ Air Bleed Blocked:</strong> When the main or idle air bleed is clogged, no air enters the fuel passage to form an emulsion. Raw liquid fuel exits the discharge nozzle — poor atomization and an over-rich mixture in the affected circuit. Rough running, sooty plugs, fuel smell.`,
};

function updateProcessDescription() {
    const desc = document.getElementById('process-description');
    if (state.currentProcess && PROCESS_DESCRIPTIONS[state.currentProcess]) {
        desc.innerHTML = PROCESS_DESCRIPTIONS[state.currentProcess];
    }
}

// ===== MIXTURE LAB =====
// ===== POWERPLANT & BERNOULLI LAB =====
function setupPowerplantLab() {
    const throttleSlider = document.getElementById('throttle-slider');
    const mixtureSlider = document.getElementById('mixture-slider');
    
    if (throttleSlider) {
        throttleSlider.addEventListener('input', () => {
            state.throttle = parseInt(throttleSlider.value);
            updatePowerplantPhysics();
        });
    }

    if (mixtureSlider) {
        mixtureSlider.addEventListener('input', () => {
            state.mixture = parseInt(mixtureSlider.value);
            updatePowerplantPhysics();
        });
    }

    // INTERACTION HINT for Lab
    const labHintKey = 'carb-hint-mixture-lab-v2';
    if (!localStorage.getItem(labHintKey)) {
        const hint = document.createElement('div');
        hint.className = 'interaction-hint';
        hint.innerHTML = `
            <div class="hint-body">
                <span class="hint-arrow-icon">▶</span>
                <span>Manipulate cockpit levers for physics</span>
            </div>
            <div class="hint-sub">SIMULATION: BERNOULLI LAB</div>
        `;
        document.body.appendChild(hint);
        localStorage.setItem(labHintKey, 'true');
        setTimeout(() => hint.remove(), 3000);
    }

    // Initial draw
    updatePowerplantPhysics();
}

/**
 * Live physics calculations for the Bernoulli lab
 */
function updatePowerplantPhysics() {
    const throttle = state.throttle;
    const mixture = state.mixture;
    
    // 1. Bernoulli Calculations
    // Air velocity is a function of throttle (not linear, squared effect)
    const velocity = Math.floor(20 + (Math.pow(throttle / 100, 1.5) * 180)); // 20-200 kts
    
    // Pressure drop (Bernoulli: ΔP ∝ v²)
    // 1" Hg drop per 40kts roughly
    const pressureDrop = (Math.pow(velocity / 100, 2) * 5.2).toFixed(1);
    
    // Fuel Air Ratio
    // Stoichiometric is 1:15. Rich is 1:12, Lean is 1:18.
    // Mixture slider 0 (ICO) to 100 (Full Rich)
    let faNum;
    if (mixture < 5) faNum = 0;
    else faNum = (18 - (mixture / 100) * 6).toFixed(1);
    const faRatio = faNum > 0 ? `1:${faNum}` : 'CUTOFF';

    // Update UI
    const velEl = document.getElementById('readout-velocity');
    const pressEl = document.getElementById('readout-pressure');
    const mixEl = document.getElementById('readout-mixture');
    
    if (velEl) velEl.textContent = `${velocity} kts`;
    if (pressEl) pressEl.textContent = `-${pressureDrop}" Hg`;
    if (mixEl) mixEl.textContent = faRatio;

    // Update 3D Model
    import('./scene.js').then(m => m.setThrottlePosition(throttle));

    // Update Graphs
    drawPowerplantGraphs(throttle, mixture);
    
    // Update Explanation
    updateLabExplanation(throttle, mixture);
}

function updateLabExplanation(throttle, mixture) {
    const explanation = document.getElementById('mixture-explanation');
    if (!explanation) return;
    
    let text = "";
    if (throttle < 15) {
        text = "<strong>Idle Phase:</strong> With the throttle nearly closed, air velocity through the venturi is too low to draw fuel from the main nozzle. Instead, high manifold vacuum pulls fuel through the idle port.";
    } else if (throttle > 85) {
        text = "<strong>Full Power:</strong> Maximum air velocity achieved. The massive pressure drop at the venturi throat draws peak fuel. The economizer valve should now be open to provide cooling.";
    } else {
        text = "<strong>Cruise:</strong> Moderate air velocity. The main metering system is fully active. Use the mixture slider to optimize for fuel economy or power.";
    }
    
    explanation.innerHTML = `<p>${text}</p>`;
}

function drawPowerplantGraphs(throttle, mixture) {
    // Reusing the drawing logic for the 2 graphs in the new lab
    drawGraph('graph-physics', throttle, 'Power/Efficiency', (t) => {
        return Math.pow(t/100, 0.7); // Simple power curve
    }, '#00f2fe');

    drawGraph('graph-egt', mixture, 'EGT', (m) => {
        if (m < 5) return 0;
        if (m < 55) return 0.3 + (m - 5) * 0.014;
        if (m < 60) return 1.0;
        return 1.0 - (m - 60) * 0.015;
    }, '#ff4b2b');
}

function drawMixtureGraphs(position) {
    drawGraph('graph-rpm', position, 'RPM', (p) => {
        if (p < 5) return 0;
        if (p < 30) return 0.3 + (p - 5) * 0.025;
        if (p < 65) return 0.9 + (p - 30) * 0.003;
        if (p < 80) return 0.95;
        return 0.93 - (p - 80) * 0.005;
    }, '#4fc3f7');

    drawGraph('graph-egt', position, 'EGT', (p) => {
        if (p < 5) return 0;
        if (p < 55) return 0.3 + (p - 5) * 0.014;
        if (p < 60) return 1.0;
        return 1.0 - (p - 60) * 0.015;
    }, '#ef4444');

    drawGraph('graph-cht', position, 'CHT', (p) => {
        if (p < 5) return 0;
        if (p < 45) return 0.4 + (p - 5) * 0.015;
        if (p < 65) return 0.95;
        return 0.95 - (p - 65) * 0.008;
    }, '#ff9800');
}

function drawGraph(canvasId, position, label, valueFn, color) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, w, h);

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,.06)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
        const y = 15 + (i / 4) * (h - 30);
        ctx.beginPath();
        ctx.moveTo(25, y);
        ctx.lineTo(w - 5, y);
        ctx.stroke();
    }

    // Curve
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 0; x <= 100; x++) {
        const px = 25 + (x / 100) * (w - 30);
        const val = valueFn(x);
        const py = h - 15 - val * (h - 30);
        x === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.stroke();

    // Current position marker
    const currentX = 25 + (position / 100) * (w - 30);
    const currentVal = valueFn(position);
    const currentY = h - 15 - currentVal * (h - 30);

    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(currentX, currentY, 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,.3)';
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(currentX, h - 15);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Labels
    ctx.fillStyle = color;
    ctx.font = 'bold 10px Inter';
    ctx.fillText(label, 5, 12);

    ctx.fillStyle = '#64748b';
    ctx.font = '8px Inter';
    ctx.fillText('Lean', 25, h - 3);
    ctx.fillText('Rich', w - 25, h - 3);
}

// ===== PART SELECTION & INFO PANEL =====
function setupInfoPanel() {
    document.getElementById('info-panel-close')?.addEventListener('click', () => {
        document.getElementById('info-panel').classList.remove('visible');
        document.body.classList.remove('panel-open');
        state.selectedPart = null;
        clearHighlights();
    });
}

function selectPart(partId) {
    if (state.processActive) return; // Prevent panel opening during process animations
    const data = getPartData(partId);
    if (!data) return;

    state.selectedPart = partId;
    clearHighlights();
    highlightPart(partId);

    // Sync Blueprint diagram if active
    if (state.blueprintActive) {
        switchToDiagramForPart(partId);
    }

    const panel = document.getElementById('info-panel');
    panel.classList.add('visible');
    document.body.classList.add('panel-open');

    const color = getSystemColor(data.system);

    document.getElementById('part-name').textContent = data.name;

    const badge = document.getElementById('part-system-badge');
    badge.textContent = color.name;
    badge.className = `badge ${data.system}`;

    document.getElementById('part-function').textContent = data.function;
    document.getElementById('part-position').textContent = data.position;
    document.getElementById('part-active').textContent = data.activePhases;
    document.getElementById('part-failures').textContent = data.failures;
    document.getElementById('part-symptoms').textContent = data.symptoms;

    // Mini animation
    drawPartAnimation(partId);

    // Mobile Auto-scroll (Disabled for fixed info-drawer on phone)
    if (window.innerWidth > 960) {
        autoScrollTo('#info-panel', 20);
    }
}

function drawPartAnimation(partId) {
    const canvas = document.getElementById('part-mini-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#1a2236';
    ctx.fillRect(0, 0, w, h);

    // Draw a simplified representation based on part system
    const data = getPartData(partId);
    if (!data) return;

    const sysColor = getSystemColor(data.system);

    ctx.fillStyle = sysColor.hex;
    ctx.font = 'bold 11px Inter';
    ctx.fillText(data.name, 8, 18);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '10px Inter';
    const shortDesc = data.function.substring(0, 120) + '...';
    wrapText(ctx, shortDesc, 8, 36, w - 16, 14);

    // System indicator bar
    ctx.fillStyle = sysColor.hex;
    ctx.fillRect(0, h - 4, w, 4);
}

function wrapText(ctx, text, x, y, maxW, lineH) {
    const words = text.split(' ');
    let line = '';
    let cy = y;
    for (const word of words) {
        const test = line + word + ' ';
        if (ctx.measureText(test).width > maxW && line) {
            ctx.fillText(line.trim(), x, cy);
            line = word + ' ';
            cy += lineH;
        } else {
            line = test;
        }
    }
    ctx.fillText(line.trim(), x, cy);
}

// ===== 3D LABELS — SEMANTIC LOD SYSTEM =====
// Zone thresholds (camera distance from model center)
const LOD_THRESHOLDS = {
    zone1_max: 14, 
    zone1_fade: 9,
    zone2_show: 10,
    zone2_max: 6,
    zone2_fade: 4,
    zone3_show: 5,
    zone3_max: 2,
};



// Raycaster for occlusion checks
const _labelRaycaster = new THREE.Raycaster();
const _modelCenter = new THREE.Vector3(0, 1.0, 0);

function getLODOpacity(zone, camDist) {
    // Generous visibility for learning
    return 1.0; 
}



// Optimization: Frame counters and state for throttling expensive tasks
let labelFrameCount = 0;
const OCCLUSION_THROTTLE = 5; // Check occlusion every N label updates
const _occlusionCache = new Map(); 

function updateLabels() {
    const overlay = document.getElementById('label-overlay');
    if (!overlay) return;

    if (!state.labelsVisible || state.mode === 'exam') {
        overlay.innerHTML = '';
        return;
    }

    const camera = getCamera();
    if (!camera) return;
    const scene = getScene();
    if (!scene) return;

    const canvas = document.getElementById('three-canvas');
    if (!canvas) return;

    const cw = canvas.clientWidth, ch = canvas.clientHeight;
    const camDist = camera.position.distanceTo(_modelCenter);

    // Collect visible labels with LOD opacity
    const candidates = [];
    for (const [partId, labelInfo] of Object.entries(LABEL_DATA)) {
        const data = getPartData(partId);
        if (!data) continue;

        // LOD opacity — skip if fully invisible at this distance
        const lodOpacity = getLODOpacity(labelInfo.zone, camDist);
        if (lodOpacity < 0.05) continue;

        // Project label position to screen
        const projected = labelInfo.pos.clone().project(camera);
        if (!isFinite(projected.x) || !isFinite(projected.y)) continue;

        const x = (projected.x * 0.5 + 0.5) * cw;
        const y = (-projected.y * 0.5 + 0.5) * ch;

        // Skip if behind camera or off-screen
        if (projected.z > 1 || x < -100 || x > cw + 100 || y < -100 || y > ch + 100) continue;

        // Project anchor point for leader line
        const anchorProj = labelInfo.anchor.clone().project(camera);
        if (!isFinite(anchorProj.x) || !isFinite(anchorProj.y)) continue;
        
        const ax = (anchorProj.x * 0.5 + 0.5) * cw;
        const ay = (-anchorProj.y * 0.5 + 0.5) * ch;

        const depth = projected.z;
        candidates.push({ 
            partId, data, x, y, ax, ay, depth, 
            lodOpacity: lodOpacity, zone: labelInfo.zone
        });

    }

    // Limit to top 10 labels to avoid "hella confusing" clutter
    candidates.sort((a, b) => a.depth - b.depth);
    const MAX_LABELS = 10; 
    const culled = candidates.slice(0, MAX_LABELS);

    // Collision avoidance
    const placed = [];
    const LABEL_W = 140, LABEL_H = 30, MARGIN = 10;

    for (const c of culled) {
        let bestX = c.x, bestY = c.y;
        let found = false;
        // More aggressive search for space
        for (let attempts = 0; attempts < 40; attempts++) {
            let overlaps = false;
            const rect = { x: bestX - LABEL_W / 2, y: bestY - LABEL_H / 2, w: LABEL_W, h: LABEL_H };
            for (const p of placed) {
                if (rect.x < p.x + p.w + MARGIN && rect.x + rect.w + MARGIN > p.x &&
                    rect.y < p.y + p.h + MARGIN && rect.y + rect.h + MARGIN > p.y) {
                    overlaps = true;
                    const angle = attempts * 0.5;
                    const dist = 30 + (attempts * 4);
                    bestX = c.x + Math.cos(angle) * dist;
                    bestY = c.y + Math.sin(angle) * dist;
                    break;
                }
            }
            if (!overlaps) { found = true; break; }
        }
        
        placed.push({ ...c, finalX: bestX, finalY: bestY, x: bestX - LABEL_W / 2, y: bestY - LABEL_H / 2, w: LABEL_W, h: LABEL_H });
    }

    let html = `<svg class="leader-lines" width="${cw}" height="${ch}" xmlns="http://www.w3.org/2000/svg">`;
    let labelsHtml = '';

    for (const p of placed) {
        const colorInfo = getSystemColor(p.data.system);
        
        // Bernoulli Physics String (if Pressure Mode is on)
        let pressureExtra = "";
        if (state.pressureVisible && (p.partId === 'venturi' || p.partId === 'carb-body' || p.partId === 'throttle-valve')) {
            const distToThroat = Math.abs(LABEL_DATA[p.partId].anchor.y - 1.76);
            const vBase = 45;
            const vScale = 1.0 + (1.5 * Math.exp(-(distToThroat * distToThroat) / 0.15));
            const velocity = Math.round(vBase * vScale);
            const pressure = (29.92 * (1 - (vScale - 1) * 0.12)).toFixed(2);
            pressureExtra = `<div class="bernoulli-data">⚡ ${velocity}kts | ${pressure}inHg</div>`;
        }

        if (isFinite(p.ax) && isFinite(p.ay) && isFinite(p.finalX) && isFinite(p.finalY)) {
            // Sleek animated leader lines — more prominent
            html += `<line x1="${p.ax.toFixed(1)}" y1="${p.ay.toFixed(1)}" x2="${p.finalX.toFixed(1)}" y2="${p.finalY.toFixed(1)}" 
                     stroke="${colorInfo.hex}" stroke-opacity="0.9" stroke-width="2" stroke-dasharray="4,4">
                       <animate attributeName="stroke-dashoffset" from="32" to="0" dur="2s" repeatCount="indefinite" />
                     </line>`;
            // Pulsing anchor point
            html += `<circle cx="${p.ax.toFixed(1)}" cy="${p.ay.toFixed(1)}" r="4" fill="${colorInfo.hex}">
                       <animate attributeName="r" values="4;6;4" dur="1.5s" repeatCount="indefinite" />
                       <animate attributeName="fill-opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />
                     </circle>`;
        }

        labelsHtml += `<div class="part-label zone-${p.zone}" 
                        style="left:${p.finalX.toFixed(1)}px;top:${p.finalY.toFixed(1)}px;border-color:${colorInfo.hex}cc;" 
                        data-part-id="${p.partId}">
                        <div class="label-main">${p.data.name}</div>
                        ${pressureExtra}
                      </div>`;
    }

    html += '</svg>';
    overlay.innerHTML = html + labelsHtml;

    // Premium Telemetry Update (if in process mode)
    if (state.processActive) {
        updateProcessTelemetry();
    }

    labelFrameCount++;
}

/**
 * Generates real-time diagnostic numbers for the Process HUD
 */
function updateProcessTelemetry() {
    const telemetry = document.getElementById('process-telemetry');
    if (!telemetry) return;

    let rpm = 0, fuelFlow = 0, airFlow = 0;
    const p = state.currentProcess;

    if (p === 'cruise') { rpm = 2450; fuelFlow = 9.2; airFlow = 185; }
    else if (p === 'idle') { rpm = 650; fuelFlow = 0.8; airFlow = 32; }
    else if (p === 'full-power') { rpm = 2700; fuelFlow = 14.5; airFlow = 210; }
    else if (p.includes('fail')) { rpm = 1800; fuelFlow = 4.2; airFlow = 120; }
    else { rpm = Math.round(1000 + Math.sin(Date.now()*0.001)*200); fuelFlow = (rpm/300).toFixed(1); airFlow = Math.round(rpm/12); }

    // Add some "jitter" for realism
    rpm += Math.round(Math.random() * 10 - 5);
    
    telemetry.innerHTML = `
        <div class="telemetry-stat"><span class="t-label">ENGINE RPM</span><span class="t-value">${rpm}</span></div>
        <div class="telemetry-stat"><span class="t-label">FUEL FLOW</span><span class="t-value">${fuelFlow} GPH</span></div>
        <div class="telemetry-stat"><span class="t-label">INTAKE PRE</span><span class="t-value">${(29.92 - (rpm/500)).toFixed(2)} inHg</span></div>
    `;
}

/**
 * Event delegation for labels to support X-Ray hover and sync
 */
function setupTroubleshootingEvents() {
    const list = document.getElementById('symptom-list');
    list.addEventListener('click', (e) => {
        const card = e.target.closest('.symptom-action-card');
        if (card) {
            const sid = card.dataset.symptomId;
            renderDiagnosis(document.getElementById('diagnosis-panel'), sid);
            
            // Trigger 3D simulation
            const symptom = SYMPTOMS.find(s => s.id === sid);
            if (symptom && symptom.visualAction) {
                simulateSymptom(symptom.visualAction);
            }
            
            // Visual feedback on card
            document.querySelectorAll('.symptom-action-card').forEach(c => c.classList.remove('active'));
            card.classList.add('active');
        }
    });
}

function setupValvesEvents() {
    const list = document.getElementById('valves-interaction-list');
    if (!list) return;
    
    list.addEventListener('click', (e) => {
        const item = e.target.closest('.valve-list-item');
        if (item) {
            const vid = item.dataset.valveId;
            renderValveDetail(document.getElementById('valve-detail-display'), vid);
            
            // Snap camera
            const valve = VALVES_DATA.find(v => v.id === vid);
            if (valve && valve.camera) {
                snapCameraToPart(valve.camera);
            }
            
            // Highlight list item
            document.querySelectorAll('.valve-list-item').forEach(i => i.classList.remove('active'));
            item.classList.add('active');
        }
    });
}


// Global accessor for label clicks
window.__selectPart = (partId) => selectPart(partId);

// ===== FLOW PARTICLE ANIMATION SYSTEM =====

let particleGroup = null;
let particleCurves = [];
let particleDots = [];
let processTime = 0;
let playSpeed = 1;

// Define flow paths for each process. Each path is an array of {points, color, count, speed}
const FLOW_PATHS = {
    'cruise': [
        // Air path: bottom flare → through venturi → out top (intake manifold)
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 12, speed: 1.0 },
        // Fuel: bowl → main jet → discharge nozzle → venturi throat
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 6, speed: 0.6 },
        // Main air bleed (now at bottom flare area)
        { points: [[-0.15,-0.02,-0.2],[-0.1,0.5,-0.12],[-0.05,1.2,-0.05],[0,1.6,0]], color: 0x90CAF9, count: 3, speed: 0.5 },
    ],
    'idle': [
        // Air through barrel (slow, throttle nearly closed at top)
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.72,0]], color: 0x90CAF9, count: 4, speed: 0.3 },
        // Idle fuel: bowl → idle jet → idle passage → idle port (at top throttle)
        { points: [[0.15,-1.2,0.2],[0.25,-0.3,0.25],[0.35,-0.3,0.3],[0.5,1.5,0.35],[0.55,2.5,0.4],[0.6,2.7,0.42]], color: 0xEF5350, count: 5, speed: 0.5 },
        // Idle air bleed
        { points: [[0.2,-0.02,0.15],[0.25,0.8,0.2],[0.3,1.5,0.25],[0.35,2.2,0.3]], color: 0x90CAF9, count: 2, speed: 0.4 },
    ],
    'full-power': [
        // High-speed air through venturi
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 18, speed: 1.5 },
        // Main fuel path (heavy flow)
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 10, speed: 0.8 },
        // Economizer path (extra fuel)
        { points: [[-0.35,1.76+0.3,0.1],[-0.45,1.76,0.12],[-0.5,1.0,0.15],[-0.45,0.5,0.12],[-0.35,-0.2,0.08]], color: 0xFFA726, count: 4, speed: 0.5 },
    ],
    'accel': [
        // Air (normal)
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 10, speed: 1.2 },
        // Accel pump discharge (burst)
        { points: [[0.3,-1.2,-0.3],[0.5,-0.6,-0.4],[1.1,-0.5,-0.6],[1.0,0,-0.5],[0.35,1.96,-0.25],[0.15,1.86,-0.05]], color: 0xFFEE58, count: 8, speed: 1.8 },
        // Main fuel (catching up)
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 4, speed: 0.5 },
    ],
    'accel-fail': [
        // Air (fast opening)
        { points: [[0,3.5,0],[0,2.8,0],[0,1.44,0],[0,0.5,0],[0,-0.1,0]], color: 0x90CAF9, count: 14, speed: 1.5 },
        // Main fuel (delayed, slow)
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.3,0],[0,0.8,0],[0,1.44,0]], color: 0xEF5350, count: 2, speed: 0.3 },
        // NO accel pump particles — that's the failure
    ],
    'mixture': [
        // Air
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 8, speed: 1.0 },
        // Fuel through mixture control valve
        { points: [[0,-1.2,0],[0,-0.35,0],[-0.15,-0.15,0.12],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 5, speed: 0.5 },
    ],
    'float-stuck-high': [
        // Air (normal)
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 6, speed: 0.8 },
        // EXCESSIVE fuel — lots of particles, fast
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 16, speed: 1.2 },
        // Fuel overflowing
        { points: [[0.6,-0.05,-0.3],[0.5,-0.3,-0.3],[0.3,-0.6,-0.2],[0,-1.0,0]], color: 0xFFEE58, count: 4, speed: 0.4 },
    ],
    'float-leaking': [
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 6, speed: 0.9 },
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 10, speed: 0.8 },
    ],
    'needle-not-sealing': [
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 6, speed: 0.9 },
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 12, speed: 0.9 },
    ],
    'main-jet-blocked': [
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 10, speed: 1.0 },
        // Very few fuel particles — jet is blocked
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xEF5350, count: 1, speed: 0.3 },
    ],
    'idle-jet-blocked': [
        { points: [[0,3.5,0],[0,2.8,0],[0,1.44,0],[0,0.64,0],[0,-0.1,0]], color: 0x90CAF9, count: 4, speed: 0.3 },
        // No idle fuel particles — idle jet is blocked
    ],
    'air-bleed-blocked': [
        { points: [[0,-0.1,0],[0,0.5,0],[0,1.76,0],[0,2.8,0],[0,3.5,0]], color: 0x90CAF9, count: 8, speed: 1.0 },
        // Raw liquid fuel (no air bleed emulsion)
        { points: [[0,-1.2,0],[0,-0.35,0],[0,0.5,0],[0,1.2,0],[0,1.76,0]], color: 0xFFEE58, count: 8, speed: 0.7 },
    ],
};

function buildFlowParticles(processId) {
    // Remove old particles
    clearFlowParticles();

    const paths = FLOW_PATHS[processId];
    if (!paths) return;

    const scene = getScene();
    particleGroup = new THREE.Group();
    particleGroup.name = 'flow-particles';
    particleCurves = [];
    particleDots = [];

    for (const path of paths) {
        const pts = path.points.map(p => new THREE.Vector3(p[0], p[1], p[2]));
        const curve = new THREE.CatmullRomCurve3(pts);
        
        const densityMultiplier = 3.0; // TRIPLE the particles for visibility
        const actualCount = Math.floor(path.count * densityMultiplier);

        for (let i = 0; i < actualCount; i++) {
            const dotGeo = new THREE.SphereGeometry(0.1, 12, 12); 
            const dotMat = new THREE.MeshBasicMaterial({
                color: path.color,
                transparent: true,
                opacity: 0.95,
                blending: THREE.AdditiveBlending,
            });
            const dot = new THREE.Mesh(dotGeo, dotMat);
            
            const spriteMat = new THREE.SpriteMaterial({
                color: path.color,
                transparent: true,
                opacity: 1.0, // MAX GLOW
                blending: THREE.AdditiveBlending,
            });
            const sprite = new THREE.Sprite(spriteMat);
            sprite.scale.set(0.5, 0.5, 0.5); // LARGE GLOW
            dot.add(sprite);

            const offset = (i / actualCount); 
            dot.userData._curveOffset = offset;
            dot.userData._speed = (path.speed || 1.0) * (0.8 + Math.random() * 0.4); 
            dot.userData._curve = curve;
            
            // Fix: more precise system categorization
            const isFuel = processId.includes('float') || processId.includes('jet') || path.color > 0xFFAA00;
            dot.userData._system = isFuel ? 'fuel' : 'air';
            dot.userData._seed = 1.0 + Math.random() * 0.5;

            particleDots.push(dot);
            particleGroup.add(dot);
        }
        particleCurves.push(curve);
    }

    scene.add(particleGroup);
}

function clearFlowParticles() {
    if (particleGroup) {
        const scene = getScene();
        scene.remove(particleGroup);
        particleGroup.traverse(c => {
            if (c.geometry) c.geometry.dispose();
            if (c.material) c.material.dispose();
        });
        particleGroup = null;
        particleDots = [];
        particleCurves = [];
    }
}

function updateFlowParticles(time) {
    if (!state.currentProcess || !particleDots.length) return;

    // Use a slightly faster base speed for better visibility of motion
    if (!playSpeed || isNaN(playSpeed)) playSpeed = 1.0;
    const dt = 0.0015 * playSpeed * (state.processPlaying ? 1 : 0); 
    if (dt === 0) return;

    processTime += dt;

    const slider = document.getElementById('timeline-slider');
    const timeLabel = document.getElementById('timeline-time');
    const pct = Math.floor((processTime % 1) * 100);
    
    if (slider && parseInt(slider.value) !== pct) {
        slider.value = pct;
    }
    
    if (timeLabel && Math.floor(time / 100) % 5 === 0) { 
        const secs = Math.floor(processTime * 10) % 60;
        const mins = Math.floor(processTime * 10 / 60);
        timeLabel.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    for (const dot of particleDots) {
        const curve = dot.userData._curve;
        if (!curve) continue;

        const currentOffset = dot.userData._curveOffset || 0;
        const distToVenturi = Math.abs(dot.position.y - 1.76);
        const bernoulli = 1.0 + (3.0 * Math.exp(-(distToVenturi * distToVenturi) / 0.15));
        const pressureFactor = (bernoulli - 1) / 3.0; // Define pressure factor for visual scaling
        
        let dynamicSpeed = 1.0;
        const lab = document.getElementById('mixture-lab');
        if (state.currentPage === 'carburetor' && lab && !lab.classList.contains('hidden')) {
             dynamicSpeed = 0.3 + ((state.throttle || 50) / 100) * 3.7;
        }

        const advance = dt * (dot.userData._speed || 1.0) * bernoulli * dynamicSpeed;
        dot.userData._curveOffset = (currentOffset + advance) % 1;
        
        const pos = curve.getPoint(dot.userData._curveOffset);
        if (pos && !isNaN(pos.x)) {
            dot.position.copy(pos);
            const pulse = 0.8 + 0.4 * Math.sin(processTime * 12 + (dot.userData._curveOffset * Math.PI * 4));
            const mixFactor = (state.mixture < 10 && dot.userData._system === 'fuel') ? 0.05 : 1; 
            
            dot.scale.setScalar(Math.max(0.15, (1.2 - (pressureFactor * 0.4)) * (dot.userData._seed || 1) * pulse * mixFactor));
            if (dot.material) {
                dot.material.opacity = Math.max(0.2, 0.95 * pulse * mixFactor);
            }
        }
    }


}

// ===== FUEL SYSTEM PAGE =====
function setupFuelSystem() {
    const canvas = document.getElementById('fuel-schematic-canvas');
    if (!canvas) return;

    canvas.addEventListener('click', (e) => {
        const compId = getFuelComponentAt(canvas, e.clientX, e.clientY);
        if (compId) {
            state.fuelSelectedComponent = compId;
            showFuelComponentInfo(compId);
        }
    });

    canvas.addEventListener('mousemove', (e) => {
        const compId = getFuelComponentAt(canvas, e.clientX, e.clientY);
        canvas.style.cursor = compId ? 'pointer' : 'default';
    });

    // Fuel animation buttons
    document.querySelectorAll('.fuel-anim-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('.fuel-anim-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            state.fuelAnim = btn.dataset.fuelAnim;
        });
    });
}

function updateFuelSchematic(time) {
    if (state.currentPage !== 'fuel-system') return;
    const canvas = document.getElementById('fuel-schematic-canvas');
    if (!canvas) return;
    drawFuelSchematic(canvas, state.fuelAnim, state.fuelSelectedComponent, time);
}

function showFuelComponentInfo(compId) {
    const comp = FUEL_COMPONENTS[compId];
    if (!comp) return;

    document.getElementById('fuel-component-title').textContent = comp.name;
    document.getElementById('fuel-component-detail').innerHTML = `
    <h4>Function</h4>
    <p>${comp.function}</p>
    <h4>Typical Location</h4>
    <p>${comp.location}</p>
    <h4>⚠️ Failure Modes</h4>
    <p>${comp.failures}</p>
    <h4>🔍 Symptoms</h4>
    <p>${comp.symptoms}</p>
  `;

    // Mobile Auto-scroll for fuel system
    autoScrollTo('#fuel-info-panel', 20);
}

// ===== TROUBLESHOOTING =====
function setupTroubleshooting() {
    const symptomContainer = document.getElementById('symptom-list');
    if (!symptomContainer || symptomContainer.dataset.init === 'true') return;

    renderSymptomList(symptomContainer);
    symptomContainer.dataset.init = 'true';

    const page = document.getElementById('page-troubleshooting');
    if (page) {
        page.onclick = (e) => {
            // Handle Symptom Card Clicks
            const card = e.target.closest('.symptom-action-card');
            if (card) {
                document.querySelectorAll('.symptom-action-card').forEach(c => c.classList.remove('active'));
                card.classList.add('active');

                const diagPanel = document.getElementById('diagnosis-panel');
                if (diagPanel) {
                    renderDiagnosis(diagPanel, card.dataset.symptomId);
                    // Isolate failure in 3D
                    import('./scene.js').then(m => m.simulateSymptom(card.dataset.symptomId));
                    // Mobile Auto-collapse
                    if (window.innerWidth <= 960) {
                        document.getElementById('symptom-list')?.classList.add('collapsed');
                    }
                    autoScrollTo('#diagnosis-panel', 20);
                }
                return;
            }

            // Mobile Collapsible Toggle via Header
            const header = e.target.closest('#symptom-list h3');
            if (header && window.innerWidth <= 960) {
                document.getElementById('symptom-list')?.classList.toggle('collapsed');
            }
        };
    }

    // Mixture diagnosis
    const mixDiag = document.getElementById('mixture-diag-grid');
    if (mixDiag) renderMixtureDiagnosis(mixDiag);
}

// ===== GLOSSARY =====
function setupGlossary() {
    const glossaryList = document.getElementById('glossary-list');
    if (glossaryList) renderGlossary(glossaryList);

    const searchInput = document.getElementById('glossary-search-input');
    if (searchInput) {
        searchInput.addEventListener('input', () => {
            const list = document.getElementById('glossary-list');
            if (list) renderGlossary(list, searchInput.value);
        });
    }
}

// ===== QUIZ =====
function setupQuiz() {
    const startBtn = document.getElementById('start-quiz-btn');
    if (!startBtn) return;

    // Update available count on filter change
    const updateCount = () => {
        const cat = document.getElementById('quiz-category').value;
        const diff = document.getElementById('quiz-difficulty').value;
        const count = getQuestionCount(cat, diff);
        const label = document.getElementById('quiz-available-count');
        if (label) label.textContent = `${count} questions available`;
    };
    document.getElementById('quiz-category')?.addEventListener('change', updateCount);
    document.getElementById('quiz-difficulty')?.addEventListener('change', updateCount);
    document.getElementById('shuffle-quiz-btn')?.addEventListener('click', () => {
        const cat = document.getElementById('quiz-category').value;
        const diff = document.getElementById('quiz-difficulty').value;
        const num = parseInt(document.getElementById('quiz-count').value);
        const btn = document.getElementById('shuffle-quiz-btn');
        if (btn) btn.classList.add('spinning');
        setTimeout(() => btn?.classList.remove('spinning'), 600);
        startQuiz(cat, diff, num);
        showQuestion();
    });
    updateCount();

    const handleNextQuestion = () => {
        const q = nextQuestion();
        if (q) {
            showQuestion();
        } else {
            // Quiz complete
            const card = document.getElementById('quiz-question-card');
            const analytics = getAnalytics();
            const scoreVal = document.getElementById('score-value').textContent;
            const scoreTotal = document.getElementById('score-total').textContent;
            const wrongs = getWrongAnswers();

            let wrongHTML = '';
            if (wrongs.length > 0) {
                wrongHTML = `
                    <div class="wrong-review">
                        <h4>❌ Questions You Got Wrong (${wrongs.length})</h4>
                        <div class="wrong-review-list">
                            ${wrongs.map((w, i) => `
                                <div class="wrong-review-item">
                                    <div class="wrong-q-num">${i + 1}.</div>
                                    <div class="wrong-q-body">
                                        <div class="wrong-q-text">${w.question}</div>
                                        <div class="wrong-q-your">Your answer: <span class="wrong-your">${w.yourAnswer}</span></div>
                                        <div class="wrong-q-correct">Correct answer: <span class="wrong-correct">${w.correctAnswer}</span></div>
                                        <div class="wrong-q-explain">${w.explanation}</div>
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `;
            } else {
                wrongHTML = '<div class="wrong-review perfect"><h4>🏆 Perfect Score! You got every question right!</h4></div>';
            }

            card.innerHTML = `
                <div class="q-num">🎉 Quiz Complete!</div>
                <div class="q-text">Your final score: ${scoreVal} / ${scoreTotal}</div>
                <div class="quiz-complete-stats">
                    <div class="complete-stat"><span>⚡</span> Total XP: ${analytics.xp}</div>
                    <div class="complete-stat"><span>🏆</span> Level: ${analytics.level}</div>
                    <div class="complete-stat"><span>🎯</span> All-time Accuracy: ${analytics.accuracy}%</div>
                </div>
                ${wrongHTML}
                <p style="margin-top:16px;color:#94a3b8;">Click "Start Quiz" to try again with different questions.</p>
            `;
            updateQuizStats();
            drawAnalyticsCharts();
            // Immediate sync so admin page shows results instantly
            syncQuizToUser();
        }
    };

    const handleHint = () => {
        const hint = getHint();
        if (hint) {
            const hintEl = document.getElementById('quiz-hint-text');
            if (hintEl) {
                hintEl.classList.remove('hidden');
                hintEl.textContent = hint.text;
                // Visual hint — pulse related part on 3D model
                if (hint.level === 2 && hint.relatedPart) {
                    highlightPart(hint.relatedPart);
                }
            }
        }
    };

    startBtn.addEventListener('click', () => {
        const category = document.getElementById('quiz-category').value;
        const difficulty = document.getElementById('quiz-difficulty').value;
        const count = parseInt(document.getElementById('quiz-count').value);
        const success = startQuiz(category, difficulty, count);
        if (!success) {
            alert("No questions found for this combination! Try a different topic or difficulty.");
            return;
        }

        // Restore the original card structure (it gets destroyed by quiz-complete innerHTML)
        const card = document.getElementById('quiz-question-card');
        card.innerHTML = `
            <div class="q-header">
              <div id="quiz-question-num" class="q-num"></div>
              <span id="quiz-difficulty-badge" class="difficulty-badge"></span>
            </div>
            <div id="quiz-question-text" class="q-text"></div>
            <div id="quiz-options" class="q-options"></div>
            <div class="q-actions">
              <button id="quiz-hint-btn" class="hint-btn" title="Get a hint (reduces XP earned)">💡 Hint</button>
              <div id="quiz-hint-text" class="hint-text hidden"></div>
            </div>
            <div id="quiz-feedback" class="q-feedback hidden"></div>
            <div id="quiz-xp-popup" class="xp-popup hidden"></div>
            <button id="quiz-next-btn" class="primary-btn hidden">Next Question →</button>
        `;

        // Re-attach listeners to new elements
        document.getElementById('quiz-next-btn')?.addEventListener('click', handleNextQuestion);
        document.getElementById('quiz-hint-btn')?.addEventListener('click', handleHint);

        showQuestion();
        document.getElementById('quiz-welcome').style.display = 'none';
        document.getElementById('quiz-score').classList.remove('hidden');
        document.getElementById('score-value').textContent = '0';
        updateQuizStats();
        // Close analytics drawer if open on mobile
        document.getElementById('quiz-analytics')?.classList.remove('mobile-active');
    });

    // Initial setup for the buttons that already exist in the HTML
    document.getElementById('quiz-next-btn')?.addEventListener('click', handleNextQuestion);
    document.getElementById('quiz-hint-btn')?.addEventListener('click', handleHint);


    // Initial stats & charts
    updateQuizStats();
    drawAnalyticsCharts();
}

function updateQuizStats() {
    const a = getAnalytics();
    const xpEl = document.getElementById('stat-xp');
    const levelEl = document.getElementById('stat-level');
    const streakEl = document.getElementById('stat-streak');
    const accEl = document.getElementById('stat-accuracy');
    const xpBar = document.getElementById('xp-bar-fill');
    const xpLabel = document.getElementById('xp-bar-label');

    if (xpEl) xpEl.textContent = a.xp;
    if (levelEl) levelEl.textContent = a.level;
    if (streakEl) streakEl.textContent = a.streakDays;
    if (accEl) accEl.textContent = `${a.accuracy}%`;

    if (xpBar && xpLabel) {
        const currentLevelXp = a.xp % 100;
        xpBar.style.width = `${currentLevelXp}%`;
        xpLabel.textContent = `${currentLevelXp} / 100 XP to next level`;
    }
}

function showQuestion() {
    const q = getCurrentQuestion();
    if (!q) return;

    const card = document.getElementById('quiz-question-card');
    card.classList.remove('hidden');

    document.getElementById('quiz-question-num').textContent = `Question ${q.number} of ${q.total}`;
    document.getElementById('quiz-question-text').textContent = q.question;
    document.getElementById('score-total').textContent = q.total;

    // Difficulty badge
    const badge = document.getElementById('quiz-difficulty-badge');
    if (badge) {
        const colors = { beginner: '🟢', intermediate: '🟡', advanced: '🔴' };
        badge.textContent = `${colors[q.difficulty] || ''} ${q.difficulty}`;
        badge.className = `difficulty-badge ${q.difficulty}`;
    }

    // Reset hint
    document.getElementById('quiz-hint-text')?.classList.add('hidden');
    document.getElementById('quiz-hint-btn')?.classList.remove('hidden');

    const optionsDiv = document.getElementById('quiz-options');
    optionsDiv.innerHTML = '';

    q.options.forEach((opt, i) => {
        const btn = document.createElement('button');
        btn.className = 'quiz-option';
        btn.textContent = `${String.fromCharCode(65 + i)}. ${opt}`;
        btn.addEventListener('click', () => handleAnswer(i));
        optionsDiv.appendChild(btn);
    });

    document.getElementById('quiz-feedback').classList.add('hidden');
    document.getElementById('quiz-next-btn').classList.add('hidden');
    document.getElementById('quiz-xp-popup')?.classList.add('hidden');
}

function handleAnswer(index) {
    const result = submitAnswer(index);

    // Disable all options
    document.querySelectorAll('.quiz-option').forEach((btn, i) => {
        btn.classList.add('disabled');
        if (i === result.correctIndex) btn.classList.add('correct');
        if (i === index && !result.correct) btn.classList.add('incorrect');
    });

    // Hide hint button
    document.getElementById('quiz-hint-btn')?.classList.add('hidden');

    // Show feedback
    const feedback = document.getElementById('quiz-feedback');
    feedback.classList.remove('hidden', 'correct-feedback', 'incorrect-feedback');
    feedback.classList.add(result.correct ? 'correct-feedback' : 'incorrect-feedback');
    feedback.innerHTML = `<strong>${result.correct ? '✅ Correct!' : '❌ Not quite.'}</strong><br>${result.explanation}`;

    // XP popup animation
    if (result.correct && result.xpGained > 0) {
        const popup = document.getElementById('quiz-xp-popup');
        if (popup) {
            popup.classList.remove('hidden');
            popup.textContent = `+${result.xpGained} XP`;
            popup.classList.add('animate');
            setTimeout(() => popup.classList.remove('animate'), 1500);
        }
    }

    // Level up notification
    if (result.leveledUp) {
        const feedback2 = document.getElementById('quiz-feedback');
        feedback2.innerHTML += `<br><strong style="color:#ffd700;">🎉 Level Up! You are now Level ${result.level}!</strong>`;
    }

    document.getElementById('score-value').textContent = result.score;
    document.getElementById('quiz-next-btn').classList.remove('hidden');

    // Mobile Auto-scroll
    autoScrollTo('#quiz-feedback', 40);

    // Update stats + charts live
    updateQuizStats();
    drawAnalyticsCharts();
    
    // Push quiz data to the user registry immediately
    syncQuizToUser();
}

// ===== ANALYTICS CHARTS =====
function drawAnalyticsCharts() {
    drawMasteryRadar();
    drawProgressionChart();
    drawDifficultyBars();
    drawTopicBars();
}

function drawMasteryRadar() {
    const canvas = document.getElementById('mastery-radar');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const cx = w / 2, cy = h / 2 + 10;
    const r = Math.min(w, h) / 2 - 30;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, w, h);

    const mastery = getTopicMastery();
    const topics = Object.entries(mastery);
    if (topics.length === 0) return;

    const n = topics.length;
    const angleStep = (Math.PI * 2) / n;

    // Draw grid rings
    for (let ring = 1; ring <= 4; ring++) {
        const ringR = (ring / 4) * r;
        ctx.strokeStyle = 'rgba(255,255,255,.08)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0; i <= n; i++) {
            const a = i * angleStep - Math.PI / 2;
            const x = cx + Math.cos(a) * ringR;
            const y = cy + Math.sin(a) * ringR;
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = 'rgba(255,255,255,.1)';
    for (let i = 0; i < n; i++) {
        const a = i * angleStep - Math.PI / 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
        ctx.stroke();
    }

    // Draw data polygon
    ctx.fillStyle = 'rgba(79,195,247,.15)';
    ctx.strokeStyle = 'rgba(79,195,247,.7)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    topics.forEach(([_, data], i) => {
        const a = i * angleStep - Math.PI / 2;
        const val = data.mastery / 100;
        const x = cx + Math.cos(a) * r * val;
        const y = cy + Math.sin(a) * r * val;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw data points & labels
    topics.forEach(([_, data], i) => {
        const a = i * angleStep - Math.PI / 2;
        const val = data.mastery / 100;
        const x = cx + Math.cos(a) * r * val;
        const y = cy + Math.sin(a) * r * val;

        // Point
        ctx.fillStyle = '#4fc3f7';
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();

        // Label
        const lx = cx + Math.cos(a) * (r + 16);
        const ly = cy + Math.sin(a) * (r + 16);
        ctx.fillStyle = '#94a3b8';
        ctx.font = '8px Inter';
        ctx.textAlign = a > -0.1 && a < Math.PI + 0.1 ? 'left' : 'right';
        if (Math.abs(a + Math.PI / 2) < 0.2) ctx.textAlign = 'center';

        // Truncate long names
        const shortName = data.name.length > 12 ? data.name.substring(0, 11) + '…' : data.name;
        ctx.fillText(shortName, lx, ly);
    });
}

function drawProgressionChart() {
    const canvas = document.getElementById('progression-chart');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, w, h);

    const data = getLearningProgression();
    if (data.length === 0) {
        ctx.fillStyle = '#64748b';
        ctx.font = '11px Inter';
        ctx.textAlign = 'center';
        ctx.fillText('Complete a quiz to see progression', w / 2, h / 2);
        return;
    }

    const marginL = 30, marginR = 10, marginT = 15, marginB = 25;
    const plotW = w - marginL - marginR;
    const plotH = h - marginT - marginB;

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,.06)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
        const y = marginT + (i / 4) * plotH;
        ctx.beginPath();
        ctx.moveTo(marginL, y);
        ctx.lineTo(w - marginR, y);
        ctx.stroke();
    }

    // Line
    ctx.strokeStyle = '#4fc3f7';
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach((d, i) => {
        const x = marginL + (i / Math.max(data.length - 1, 1)) * plotW;
        const y = marginT + (1 - d.accuracy / 100) * plotH;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Area fill
    ctx.fillStyle = 'rgba(79,195,247,.1)';
    ctx.beginPath();
    data.forEach((d, i) => {
        const x = marginL + (i / Math.max(data.length - 1, 1)) * plotW;
        const y = marginT + (1 - d.accuracy / 100) * plotH;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.lineTo(marginL + plotW, marginT + plotH);
    ctx.lineTo(marginL, marginT + plotH);
    ctx.closePath();
    ctx.fill();

    // Points
    data.forEach((d, i) => {
        const x = marginL + (i / Math.max(data.length - 1, 1)) * plotW;
        const y = marginT + (1 - d.accuracy / 100) * plotH;
        ctx.fillStyle = '#4fc3f7';
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
    });

    // Labels
    ctx.fillStyle = '#64748b';
    ctx.font = '8px Inter';
    ctx.textAlign = 'left';
    ctx.fillText('100%', 2, marginT + 4);
    ctx.fillText('0%', 2, h - marginB);
    ctx.textAlign = 'center';
    ctx.fillText('Quiz Attempts →', w / 2, h - 4);
}

function drawDifficultyBars() {
    const canvas = document.getElementById('difficulty-bars');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, w, h);

    const perf = getDifficultyPerformance();
    const bars = [
        { label: 'Beginner', value: perf.beginner, color: '#22c55e' },
        { label: 'Intermediate', value: perf.intermediate, color: '#eab308' },
        { label: 'Advanced', value: perf.advanced, color: '#ef4444' },
    ];

    const barW = 60, gap = 25, startX = (w - (bars.length * barW + (bars.length - 1) * gap)) / 2;
    const maxH = h - 40;

    bars.forEach((bar, i) => {
        const x = startX + i * (barW + gap);
        const barH = (bar.value / 100) * maxH;
        const y = h - 25 - barH;

        // Bar
        ctx.fillStyle = bar.color + '33';
        ctx.fillRect(x, y, barW, barH);
        ctx.strokeStyle = bar.color;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x, y, barW, barH);

        // Value
        ctx.fillStyle = bar.color;
        ctx.font = 'bold 12px Inter';
        ctx.textAlign = 'center';
        ctx.fillText(`${bar.value}%`, x + barW / 2, y - 5);

        // Label
        ctx.fillStyle = '#94a3b8';
        ctx.font = '9px Inter';
        ctx.fillText(bar.label, x + barW / 2, h - 8);
    });
}

function drawTopicBars() {
    const container = document.getElementById('topic-mastery-bars');
    if (!container) return;

    const mastery = getTopicMastery();
    let html = '';

    for (const [cat, data] of Object.entries(mastery)) {
        const color = data.mastery > 70 ? '#22c55e' : data.mastery > 40 ? '#eab308' : '#ef4444';
        html += `
            <div class="topic-bar-row">
                <span class="topic-bar-name">${data.name}</span>
                <div class="topic-bar-track">
                    <div class="topic-bar-fill" style="width:${data.mastery}%;background:${color}"></div>
                </div>
                <span class="topic-bar-pct" style="color:${color}">${data.mastery}%</span>
            </div>
        `;
    }

    container.innerHTML = html;
}

// ===== VALVES =====
function setupValves() {
    const grid = document.getElementById('valves-grid');
    if (!grid || grid.dataset.init === 'true') return;

    renderValveCards(grid);
    grid.dataset.init = 'true';

    grid.addEventListener('click', (e) => {
        const card = e.target.closest('.valve-list-item');
        if (card) {
            const valveId = card.dataset.valveId;
            // Technical Drill-down: Render in-page
            const detailContainer = document.getElementById('valve-detail-display');
            if (detailContainer) {
                import('./valves/valves.js').then(m => m.renderValveDetail(detailContainer, valveId));
                // Isolate in 3D
                import('./scene.js').then(m => m.isolateValve(valveId));
                // Mobile Auto-scroll to technical data
                autoScrollTo('#valve-detail-display', 20);
                // Mobile Auto-collapse directory
                if (window.innerWidth <= 960) {
                    document.getElementById('valves-side-panel')?.classList.add('collapsed');
                }
            }
            return;
        }

        // Mobile Collapsible Toggle via Header
        const header = e.target.closest('#valves-side-panel h3');
        if (header && window.innerWidth <= 960) {
            document.getElementById('valves-side-panel')?.classList.toggle('collapsed');
        }
    });

    document.querySelector('.modal-close')?.addEventListener('click', () => {
        document.getElementById('valve-detail-modal').classList.add('hidden');
        activeValveAnim = null;
    });
}

let activeValveAnim = null;

function openValveModal(valveId) {
    const modal = document.getElementById('valve-detail-modal');
    const valve = getValveData(valveId);
    if (!valve || !modal) return;

    modal.classList.remove('hidden');
    document.getElementById('valve-title').textContent = `${valve.icon} ${valve.title}`;

    const infoContainer = document.getElementById('valve-info');
    renderValveDetail(infoContainer, valveId);

    activeValveAnim = valveId;
}

function updateValveAnimations(time) {
    if (!activeValveAnim) return;
    const canvas = document.getElementById('valve-detail-canvas');
    if (!canvas) return;
    drawValveAnimation(canvas, activeValveAnim, time);
}

// ===== CONTEXT INSETS =====
function drawInsetSchematics() {
    drawFuelInset();
    drawAirInset();
}

function drawFuelInset() {
    const canvas = document.getElementById('fuel-inset-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = '#c62828';
    ctx.lineWidth = 2;

    // Simplified schematic
    const items = [
        { label: 'Tanks', x: 15, y: h / 2, w: 45, h: 30 },
        { label: 'Selector', x: 75, y: h / 2, w: 45, h: 25 },
        { label: 'Strainer', x: 135, y: h / 2, w: 40, h: 25 },
        { label: 'Pump', x: 190, y: h / 2, w: 35, h: 25 },
        { label: 'CARB', x: 245, y: h / 2, w: 50, h: 30 },
    ];

    items.forEach((item, i) => {
        const isCarb = item.label === 'CARB';
        ctx.fillStyle = isCarb ? 'rgba(79,195,247,.15)' : 'rgba(198,40,40,.1)';
        ctx.strokeStyle = isCarb ? '#4fc3f7' : '#c62828';
        ctx.lineWidth = isCarb ? 2 : 1;

        ctx.fillRect(item.x, item.y - item.h / 2, item.w, item.h);
        ctx.strokeRect(item.x, item.y - item.h / 2, item.w, item.h);

        ctx.fillStyle = isCarb ? '#4fc3f7' : '#ef5350';
        ctx.font = '9px Inter';
        ctx.fillText(item.label, item.x + 3, item.y + 4);

        if (i < items.length - 1) {
            const next = items[i + 1];
            ctx.strokeStyle = '#c62828';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(item.x + item.w, item.y);
            ctx.lineTo(next.x, next.y);
            ctx.stroke();
        }
    });

    ctx.fillStyle = '#64748b';
    ctx.font = '8px Inter';
    ctx.fillText('Fuel flow direction →', 15, h - 10);
}

function drawAirInset() {
    const canvas = document.getElementById('air-inset-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, w, h);

    const items = [
        { label: 'Air\nFilter', x: 15, y: h / 2, w: 40, h: 30 },
        { label: 'Air\nBox', x: 70, y: h / 2, w: 35, h: 25 },
        { label: 'CARB', x: 120, y: h / 2, w: 50, h: 30 },
        { label: 'Intake\nManifold', x: 185, y: h / 2, w: 50, h: 25 },
        { label: 'Cylinders', x: 250, y: h / 2, w: 55, h: 30 },
    ];

    items.forEach((item, i) => {
        const isCarb = item.label === 'CARB';
        ctx.fillStyle = isCarb ? 'rgba(79,195,247,.15)' : 'rgba(79,195,247,.05)';
        ctx.strokeStyle = isCarb ? '#4fc3f7' : '#4fc3f7';
        ctx.lineWidth = isCarb ? 2 : 1;

        ctx.fillRect(item.x, item.y - item.h / 2, item.w, item.h);
        ctx.strokeRect(item.x, item.y - item.h / 2, item.w, item.h);

        ctx.fillStyle = isCarb ? '#4fc3f7' : '#81d4fa';
        ctx.font = '9px Inter';
        const lines = item.label.split('\n');
        lines.forEach((line, li) => {
            ctx.fillText(line, item.x + 3, item.y - 2 + li * 12);
        });

        if (i < items.length - 1) {
            const next = items[i + 1];
            ctx.strokeStyle = '#4fc3f7';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(item.x + item.w, item.y);
            ctx.lineTo(next.x, next.y);
            ctx.stroke();
        }
    });

    ctx.fillStyle = '#64748b';
    ctx.font = '8px Inter';
    ctx.fillText('Airflow direction →', 15, h - 10);
}

// ===== BLUEPRINT MODE =====

function setupBlueprintMode() {
    const bViewer = document.getElementById('blueprint-viewer');
    const resetBtn = document.getElementById('blueprint-zoom-reset');
    
    // Zoom listener
    bViewer.addEventListener('wheel', (e) => {
        if (!state.blueprintActive) return;
        e.preventDefault();
        
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.min(Math.max(state.blueprintZoom * delta, 0.4), 8);
        
        const rect = bViewer.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        const imgX = (mouseX - state.blueprintPan.x) / state.blueprintZoom;
        const imgY = (mouseY - state.blueprintPan.y) / state.blueprintZoom;
        
        state.blueprintZoom = newZoom;
        state.blueprintPan.x = mouseX - imgX * state.blueprintZoom;
        state.blueprintPan.y = mouseY - imgY * state.blueprintZoom;
        
        updateBlueprintTransform();
    }, { passive: false });

    // Pan listeners
    bViewer.addEventListener('mousedown', (e) => {
        if (!state.blueprintActive) return;
        isPanning = true;
        startPanMouse = { x: e.clientX, y: e.clientY };
        startPanOffset = { ...state.blueprintPan };
    });

    window.addEventListener('mousemove', (e) => {
        if (!isPanning) return;
        const dx = e.clientX - startPanMouse.x;
        const dy = e.clientY - startPanMouse.y;
        state.blueprintPan.x = startPanOffset.x + dx;
        state.blueprintPan.y = startPanOffset.y + dy;
        updateBlueprintTransform();
    });

    window.addEventListener('mouseup', () => {
        if (isPanning) isPanning = false;
    });

    resetBtn.addEventListener('click', () => {
        state.blueprintZoom = 1;
        state.blueprintPan = { x: 0, y: 0 };
        updateBlueprintTransform();
    });
}

function toggleBlueprintMode(active) {
    state.blueprintActive = active;
    const panel = document.getElementById('blueprint-panel');

    if (active) {
        panel.classList.remove('hidden');
        renderDiagram(state.currentDiagram);
        // Switch to textbook camera for better sync
        setViewPreset('textbook');
    } else {
        panel.classList.add('hidden');
    }

    // Force Three.js to resize immediately
    requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
    });
}

function renderDiagram(diagramId) {
    const data = DIAGRAMS_DATA[diagramId];
    if (!data) return;

    state.currentDiagram = diagramId;
    const img = document.getElementById('blueprint-img');
    const title = document.getElementById('blueprint-title');
    const wrap = document.getElementById('blueprint-canvas-wrap');

    img.src = data.src;
    title.textContent = data.title;

    // Reset view if switching diagrams
    state.blueprintZoom = 1;
    state.blueprintPan = { x: 0, y: 0 };
    updateBlueprintTransform();

    // Render hotspots
    const hotspotContainer = document.getElementById('blueprint-hotspots');
    hotspotContainer.innerHTML = '';
    data.hotspots.forEach(hs => {
        const h = document.createElement('div');
        h.className = 'blueprint-hotspot';
        h.style.left = hs.x + '%';
        h.style.top = hs.y + '%';
        h.style.width = hs.w + '%';
        h.style.height = hs.h + '%';
        h.title = hs.name;

        h.onmouseenter = () => highlightPart(hs.partId);
        h.onmouseleave = () => clearHighlights();
        h.onclick = () => selectPart(hs.partId);

        hotspotContainer.appendChild(h);
    });
}

function updateBlueprintTransform() {
    const wrap = document.getElementById('blueprint-canvas-wrap');
    if (wrap) {
        wrap.style.transform = `translate(${state.blueprintPan.x}px, ${state.blueprintPan.y}px) scale(${state.blueprintZoom})`;
    }
}

function switchToDiagramForPart(partId) {
    const diagramId = PART_TO_DIAGRAM[partId];
    if (diagramId && diagramId !== state.currentDiagram) {
        renderDiagram(diagramId);
    }
}

// ============================================================
// WELCOME MODAL — First-time visitor flow
// ============================================================

function setupWelcomeModal() {
    const modal = document.getElementById('welcome-modal');
    const input = document.getElementById('welcome-name-input');
    const btn = document.getElementById('welcome-submit-btn');
    if (!modal || !input || !btn) {
        console.warn('[Welcome] Modal elements not found in DOM');
        return;
    }

    const firstVisit = isFirstVisit();
    console.log('[Welcome] isFirstVisit:', firstVisit);

    if (firstVisit) {
        // Recognition logic
        const existingId = localStorage.getItem('carb-current-user-id');
        if (existingId && existingId.startsWith('guest_')) {
            const titleEl = modal.querySelector('h2');
            if (titleEl) titleEl.textContent = "IDENTIFIED RETURNING PILOT";
            input.placeholder = "Enter name to finalize credentials...";
            btn.textContent = "CONFIRM IDENTITY";
        }

        modal.classList.remove('hidden');
        // Force display in case CSS has issues
        modal.style.display = 'flex';
    }

    // Enable button when name has 2+ chars
    input.addEventListener('input', () => {
        const hasName = input.value.trim().length >= 2;
        btn.disabled = !hasName;
    });

    // Submit handler
    const doSubmit = () => {
        const name = input.value.trim();
        if (name.length < 2) return;

        // Admin Secret Access
        if (name.toLowerCase() === 'admin') {
            const code = prompt("Enter Admin Code:");
            if (code === "3577") {
                window.location.href = "admin.html";
                return; // Stop standard registration
            } else {
                alert("Access Denied. Incorrect code.");
                return; 
            }
        }

        setUserName(name);
        // CRITICAL: Record initial visit and state NOW that id is set
        recordVisit();
        updateUserPageVisit(state.currentPage || 'carburetor');
        // Create the session immediately in the registry
        // Also ensure quiz engine knows about the new ID for storage
        initQuizForUser();

        // Check if mobile device
        const isMobile = window.innerWidth <= 768 || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        
        if (isMobile && !modal.classList.contains('mobile-warning-shown')) {
            modal.classList.add('mobile-warning-shown'); // Prevent re-triggering logic
            const step1 = document.getElementById('welcome-step-1');
            const step2 = document.getElementById('welcome-step-mobile');
            
            if (step1 && step2) {
                step1.classList.add('hidden');
                step2.classList.remove('hidden');
                
                const understandBtn = document.getElementById('welcome-mobile-understand-btn');
                if (understandBtn) {
                    understandBtn.addEventListener('click', () => {
                        finalizeWelcome();
                    }, {once: true});
                }
                return; // Stop here, wait for click
            }
        }
        
        finalizeWelcome();
    };

    const finalizeWelcome = () => {
        // JARVIS INTELLIGENCE: Flash a confirmation
        if (input.placeholder.toLowerCase().includes('returning')) {
            btn.textContent = "IDENTITY CONFIRMED";
        }

        modal.style.opacity = '0';
        modal.style.transition = 'opacity 0.4s ease';
        setTimeout(() => {
            modal.classList.add('hidden');
            modal.style.display = 'none';
            modal.style.opacity = '';
            modal.style.transition = '';
            updateGreeting();
            setupMobileMenuHint();
        }, 400);
    };

    // Auto-focus after a beat (let animations finish)
    setTimeout(() => input.focus(), 600);

    btn.addEventListener('click', doSubmit);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') doSubmit();
    });
}

/**
 * JARVIS HUD INTELLIGENCE: 
 * Guides mobile users to the hamburger menu if they haven't seen it yet.
 */
function setupMobileMenuHint() {
    const isMobile = window.innerWidth <= 960;
    const seen = localStorage.getItem('carb-mobile-menu-hint-seen');
    
    // Only show if mobile and not seen yet
    if (!isMobile || seen === 'true') return;

    const dot = document.querySelector('.menu-hint-dot');
    const hint = document.getElementById('mobile-menu-hint');

    if (dot) dot.classList.remove('hidden');
    if (hint) {
        hint.classList.remove('hidden');
        // Auto-fade the text bubble after 3 seconds
        setTimeout(() => {
            if (hint) {
                hint.classList.add('fade-out');
                setTimeout(() => hint.classList.add('hidden'), 500);
            }
        }, 3000);
    }
}

function updateGreeting() {
    const el = document.getElementById('user-greeting');
    if (!el) return;
    const name = getUserName();
    if (!name) {
        el.innerHTML = '<span class="set-name-prompt">👋 Set your name...</span>';
    } else {
        const greeting = getGreeting();
        el.textContent = greeting;
    }
    
    // Allow clicking to change name/re-trigger welcome
    el.style.cursor = 'pointer';
    el.onclick = () => {
        const modal = document.getElementById('welcome-modal');
        if (modal) {
            modal.classList.remove('hidden');
            modal.style.display = 'flex';
            const input = document.getElementById('welcome-name-input');
            if (input) {
                if (name) input.value = name;
                setTimeout(() => input.focus(), 200);
            }
        }
    };
}

// ============================================================
// USAGE ANALYTICS TRACKER (PER-USER)
// Records page visits, time per section, session data
// into the user registry so admin sees everything per-person
// ============================================================

let _currentPageStart = Date.now();
let _currentPageName = 'carburetor';
let _sessionStart = Date.now();

// Module-level sync function so it can be called from anywhere (e.g. registration)
export function syncSession(isHeartbeat = true) {
    const id = getCurrentUserId();
    if (!id) return; // Can't sync if not logged in yet

    // Save time on current page
    const now = Date.now();
    const elapsed = now - _currentPageStart;
    if (elapsed > 0) {
        updateUserPageTime(_currentPageName, elapsed);
        _currentPageStart = now; // Reset start time for next increment
    }

    // Record/Update session
    const sessionDuration = now - _sessionStart;
    recordUserSession({
        start: new Date(_sessionStart).toISOString(),
        end: new Date().toISOString(),
        durationMs: sessionDuration,
        isHeartbeat: isHeartbeat 
    });

    // Snapshot quiz analytics
    syncQuizToUser();
}

function initUsageTracker() {
    _sessionStart = Date.now();
    _currentPageStart = Date.now();
    _currentPageName = 'carburetor';

    // Record initial page visit
    updateUserPageVisit('carburetor');

    // Track page switches via MutationObserver
    const observer = new MutationObserver(() => {
        const activePage = document.querySelector('.page.active');
        if (activePage) {
            const pageId = activePage.id.replace('page-', '');
            if (pageId !== _currentPageName) {
                // Record time on previous page (accurate per-user)
                const elapsed = Date.now() - _currentPageStart;
                updateUserPageTime(_currentPageName, elapsed);
                updateUserPageVisit(pageId);

                _currentPageName = pageId;
                _currentPageStart = Date.now();
            }
        }
    });

    const mainContent = document.getElementById('main-content');
    if (mainContent) {
        observer.observe(mainContent, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    // Initial record if user already exists
    if (!isFirstVisit()) {
        syncSession(false);
    }

    // Sync every 10 seconds (heartbeats)
    const heartbeat = setInterval(() => syncSession(true), 10000);

    // Save on visibility change (more reliable than beforeunload)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            syncSession();
        }
    });

    // Final save on unload
    window.addEventListener('beforeunload', () => syncSession());

    // Track feature usage (toolbar buttons, mode switches, etc.)
    document.addEventListener('click', (e) => {
        const toolBtn = e.target.closest('.tool-btn');
        const modeBtn = e.target.closest('.mode-btn');
        const processBtn = e.target.closest('.process-btn');
        const navBtn = e.target.closest('.nav-btn');
        let feature = null;
        if (toolBtn) feature = `tool:${toolBtn.dataset.view || toolBtn.dataset.toggle || toolBtn.dataset.system || toolBtn.dataset.camera || 'unknown'}`;
        else if (modeBtn) feature = `mode:${modeBtn.dataset.mode || 'unknown'}`;
        else if (processBtn) feature = `process:${processBtn.dataset.process || 'unknown'}`;
        else if (navBtn) feature = `nav:${navBtn.dataset.page || 'unknown'}`;
        
        if (feature) {
            updateUserFeature(feature);
        }
    });
}

// Shared stop function to ensure HUD and state stay in sync
function stopAutoShowcase() {
    lastInteractionTime = Date.now();
    if (mainControls && mainControls.autoRotate) {
        setAutoRotate(false); 
        const hud = document.getElementById('cinematic-hud');
        if (hud) hud.classList.add('hidden');
    }
}

/**
 * Monitors user interaction on the 3D canvas.
 * After 3 seconds of idleness, starts a slow cinematic rotation.
 */
function setupAutoRotateTracker() {
    const canvas = document.getElementById('three-canvas');
    if (!canvas || !mainControls) return;

    // Triggered on active interaction anywhere on screen
    const onActiveInteraction = () => {
        stopAutoShowcase();
    };

    // OrbitControls "start" event captures actual 3D drag
    mainControls.addEventListener('start', onActiveInteraction);
    // Bind global listeners so mouse movement over UI panels resets inactivity timer
    window.addEventListener('mousedown', onActiveInteraction);
    window.addEventListener('mousemove', onActiveInteraction);
    window.addEventListener('touchstart', onActiveInteraction);
    window.addEventListener('wheel', onActiveInteraction);

    // Global check interval
    setInterval(() => {
        if (!state.autoRotate) return;
        
        const idleTime = Date.now() - lastInteractionTime;
        if (idleTime > 3000) { // 3 seconds idle
            // Allow rotation on home and main carburetor view if not in a complex animation mode
            const isShowcasePage = state.currentPage === 'carburetor' || state.currentPage === 'home';
            if (state.mode !== 'process' && state.mode !== 'mixture' && isShowcasePage) {
                if (!mainControls.autoRotate) {
                    setAutoRotate(true);
                    
                    // Show cinematic HUD for that "WOW" factor
                    const hud = document.getElementById('cinematic-hud');
                    if (hud) hud.classList.remove('hidden');
                }
            }
        }
    }, 250);
}
